// ============================================================================
// MarketMasterFeedPage — мобильная лента заявок ДЛЯ МАСТЕРА (аналог profi.ru).
//
// • карточки: категория, дата, краткое описание, цена взятия, состояние
//   контактов (до взятия сервер отдаёт «скрыт до взятия»);
// • отборы: категория, «только новые»; сортировка — по свежести (сервер);
// • баланс мастера (личный счёт биллинга) + «Взять заявку» с состояниями
//   «уже взята / нет денег / ошибка / нет связи»;
// • вкладка «Мои заявки» — взятые текущим мастером + переход в чат заявки.
//
// ЖИВОЕ ОБНОВЛЕНИЕ (без ручного «Обновить»):
//   подписка на существующую шину (data.document.mkt_requests.*) → карточка
//   добавляется/обновляется/убирается В ТЕКУЩЕЙ странице (lib/marketFeedLive);
//   индикатор/звук/бейдж — по настройкам пользователя; при разрыве связи —
//   честное «приостановлено» и ТИХАЯ сверка с сервером после reconnect.
//
// Ограничения данных hydra.market (НЕ подменяются моком, зафиксированы в
// lib/marketFeed.ts): нет отдельного реквизита «Район». Признак подтверждения
// телефона приходит из серверного гейта (phone_verified/phone_unverified):
// неподтверждённый номер блокирует взятие/контакты и показывается на карточке.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList, Inbox, MapPin, RefreshCw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useConnectionStore } from '@/lib/connection';
import { eventBusHub } from '@/lib/eventBusHub';
import { logErrorToJournal } from '@/lib/errors';
import { settingsStore } from '@/lib/settingsStore';
import {
  loadMarketCategories,
  loadMarketRequest,
  loadMarketServices,
  toMarketFailure,
  type MarketCategory,
  type MarketFailure,
  type MarketRequest,
  type MarketService,
} from '@/lib/market';
import {
  EMPTY_MARKET_GEO,
  loadMarketGeoLabels,
  marketGeoFilter,
  marketGeoHasValue,
  type MarketGeoLabelMap,
} from '@/lib/marketGeo';
import { loadMyMarketGeo } from '@/lib/marketMasterArea';
import {
  EMPTY_MARKET_FEED_FILTERS,
  MARKET_FEED_PAGE_SIZE,
  isBalanceFailure,
  isTakeFailure,
  loadMarketFeed,
  loadMarketFeedAuxiliary,
  loadMasterBalance,
  marketFeedSourceKey,
  normalizeMarketFeedFilters,
  takeMarketRequest,
  type MarketFeedAuxiliary,
  type MarketFeedFilters as MarketFeedFilterValues,
  type MarketFeedItem,
  type MarketFeedPage,
  type MarketFeedScope,
  type MasterBalanceResult,
  type TakeResult,
} from '@/lib/marketFeed';
import {
  acknowledgeMarketFeedNew,
  buildMarketFeedLiveItem,
  dropMarketFeedNew,
  getMarketFeedLiveSettings,
  mergeMarketFeedLiveEvent,
  noteNewMarketRequests,
  parseMarketFeedLiveEvent,
  retainMarketFeedNew,
  useMarketFeedLiveStore,
  type MarketFeedLiveContext,
  type MarketFeedLiveEvent,
  type MarketFeedLiveSettings,
} from '@/lib/marketFeedLive';
import { MarketBalanceCard } from '@/components/market/MarketBalanceCard';
import { MarketEmpty, MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketFeedCard } from '@/components/market/MarketFeedCard';
import { MarketFeedFilters } from '@/components/market/MarketFeedFilters';
import { MarketFeedLiveBanner } from '@/components/market/MarketFeedLiveBanner';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';

/** Ключ сохранённых отборов ленты (user_settings; SSoT — сервер). */
const MARKET_FEED_FILTERS_KEY = 'market.feed.filters';

interface MarketMasterFeedPageProps {
  /** Нет «назад» — когда лента и есть корень кабинета мастера. */
  onBack?: () => void;
  onOpenChat: (request: MarketRequest) => void;
  onOpenWallet: () => void;
  /** Зона обслуживания мастера (кабинет). */
  onOpenArea: () => void;
  /** Профиль клиента/мастера: контакты, адреса, уведомления. */
  onOpenProfile: () => void;
}

export function MarketMasterFeedPage({
  onBack,
  onOpenChat,
  onOpenWallet,
  onOpenArea,
  onOpenProfile,
}: MarketMasterFeedPageProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const { t } = useI18n();
  const showSuccess = useToastStore((s) => s.showSuccess);
  const connection = useConnectionStore((s) => s.status);
  const newIds = useMarketFeedLiveStore((s) => s.newIds);

  const [scope, setScope] = useState<MarketFeedScope>('feed');
  // Отборы ленты: восстанавливаются из сохранённых настроек (см. эффект ниже).
  const [filters, setFilters] = useState<MarketFeedFilterValues>({
    ...EMPTY_MARKET_FEED_FILTERS,
    geo: { ...EMPTY_MARKET_GEO },
  });
  /** Сохранённые отборы прочитаны — можно загружать ленту/зону мастера. */
  const [filtersReady, setFiltersReady] = useState(false);
  /** Настройки получены с сервера — можно безопасно сохранять отборы. */
  const [settingsReady, setSettingsReady] = useState(false);
  /** У пользователя есть сохранённые отборы — зона мастера их не перетирает. */
  const savedFiltersRef = useRef(false);
  const geoPrefilledRef = useRef(false);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [services, setServices] = useState<MarketService[]>([]);
  const [geoLabels, setGeoLabels] = useState<MarketGeoLabelMap>({});
  const [page, setPage] = useState<MarketFeedPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [tick, setTick] = useState(0);
  /** Ошибка ФОНОВОЙ сверки: лента остаётся, сообщение неблокирующее. */
  const [syncError, setSyncError] = useState('');
  const [liveSettings, setLiveSettings] = useState<MarketFeedLiveSettings>(
    () => getMarketFeedLiveSettings(),
  );
  const liveSettingsRef = useRef<MarketFeedLiveSettings>(liveSettings);

  const [balance, setBalance] = useState<MasterBalanceResult | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceTick, setBalanceTick] = useState(0);

  const [takingId, setTakingId] = useState('');
  const [takeFailures, setTakeFailures] = useState<Record<string, TakeResult>>({});

  // Действующие отборы: «только новые» относится только к свободной ленте.
  const feedFilters = useMemo<MarketFeedFilterValues>(
    () => ({ ...filters, onlyNew: scope === 'feed' ? filters.onlyNew : false }),
    [filters, scope],
  );

  // Лента перечитывается с небольшой задержкой: ввод в поиск не должен
  // отправлять запрос на каждую букву (значение формы обновляется сразу).
  const [loadedFilters, setLoadedFilters] = useState<MarketFeedFilterValues | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    const timer = setTimeout(() => setLoadedFilters(feedFilters), 350);
    return () => clearTimeout(timer);
  }, [feedFilters, filtersReady]);

  // Актуальный контекст для живых событий (без переподписки на каждое изменение).
  const ctxRef = useRef<MarketFeedLiveContext>({ scope, userId, filters: feedFilters });
  // Зеркало страницы: события применяются к текущим данным, а не к устаревшему state.
  const pageRef = useRef<MarketFeedPage | null>(null);
  const auxRef = useRef<MarketFeedAuxiliary | null>(null);
  const liveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const listTopRef = useRef<HTMLDivElement | null>(null);
  const setFeedPage = useCallback((next: MarketFeedPage | null) => {
    pageRef.current = next;
    setPage(next);
  }, []);

  useEffect(() => {
    ctxRef.current = { scope, userId, filters: feedFilters };
  }, [feedFilters, scope, userId]);

  // ВОССТАНОВЛЕНИЕ отборов: настройки пользователя (user_settings, SSoT сервера).
  // Лента грузится только после восстановления — иначе первый запрос ушёл бы
  // с пустыми отборами и мигнул чужими заявками.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await settingsStore.hydrate();
      } catch (e) {
        // Настройки недоступны — лента работает с отборами по умолчанию.
        logErrorToJournal(e, 'Лента мастера: сохранённые отборы');
      }
      if (cancelled) return;
      // Гидрация не удалась: сохранение отключаем, чтобы не затереть
      // серверные отборы пустым набором.
      const hydrated = settingsStore.isHydrated();
      setSettingsReady(hydrated);
      if (hydrated) {
        const saved = settingsStore.getJSON<unknown>(MARKET_FEED_FILTERS_KEY, null);
        if (saved) {
          savedFiltersRef.current = true;
          geoPrefilledRef.current = true;
          setFilters(normalizeMarketFeedFilters(saved));
        }
      }
      setFiltersReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // СОХРАНЕНИЕ отборов при каждом изменении (settingsStore дебаунсит запись).
  useEffect(() => {
    if (!filtersReady || !settingsReady) return;
    settingsStore.setJSON(MARKET_FEED_FILTERS_KEY, filters);
  }, [filters, filtersReady, settingsReady]);

  // Настройки индикатора/звука/бейджа (общие user_settings; гидратация асинхронна).
  useEffect(() => {
    const apply = () => {
      const next = getMarketFeedLiveSettings();
      liveSettingsRef.current = next;
      setLiveSettings(next);
    };
    apply();
    return settingsStore.subscribe(apply);
  }, []);

  // Зона мастера — отбор по умолчанию (координаты/радиус/ссылки). Заполняется
  // ОДИН раз: сохранённые/ручные отборы пользователя не перетираются.
  useEffect(() => {
    if (!filtersReady || !userId || geoPrefilledRef.current || savedFiltersRef.current) return;
    let cancelled = false;
    void loadMyMarketGeo(userId)
      .then((masterGeo) => {
        if (cancelled || geoPrefilledRef.current) return;
        if (!masterGeo || !marketGeoHasValue(masterGeo)) return;
        geoPrefilledRef.current = true;
        setFilters((prev) => ({ ...prev, geo: masterGeo }));
      })
      .catch((e) => {
        // Зона не загрузилась — лента работает без неё; причина в журнале.
        logErrorToJournal(e, 'Лента мастера: зона обслуживания для отбора');
      });
    return () => {
      cancelled = true;
    };
  }, [filtersReady, userId]);

  // Категории и услуги — для отбора и подписей (серверные данные; ошибка только
  // в журнал, лента работает и без них).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadMarketCategories();
        if (!cancelled) setCategories(list);
      } catch (e) {
        logErrorToJournal(e, 'Лента мастера: категории отбора');
      }
      try {
        const list = await loadMarketServices();
        if (!cancelled) setServices(list);
      } catch (e) {
        logErrorToJournal(e, 'Лента мастера: услуги отбора');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Названия выбранных гео-ссылок — для сводки активных отборов.
  useEffect(() => {
    if (!marketGeoHasValue(feedFilters.geo)) {
      setGeoLabels({});
      return;
    }
    let cancelled = false;
    void loadMarketGeoLabels(feedFilters.geo)
      .then((labels) => {
        if (!cancelled) setGeoLabels(labels);
      })
      .catch((e) => {
        // Названия не загрузились — сводка покажет уровень без имени.
        logErrorToJournal(e, 'Лента мастера: названия гео для сводки отборов');
      });
    return () => {
      cancelled = true;
    };
  }, [feedFilters.geo]);

  // Лента: загрузка и фоновая сверка — асинхронная операция эффекта
  // (setState — только в колбэках загрузки, не в синхронной части эффекта).
  useEffect(() => {
    if (!filtersReady || !loadedFilters) return;
    let cancelled = false;
    void (async () => {
      try {
        const next = await loadMarketFeed(scope, userId, loadedFilters);
        if (cancelled) return;
        setFeedPage(next);
        setFailure(null);
        setSyncError('');
        // Справочники могли измениться — живые события перечитают их из кэша.
        auxRef.current = null;
        // Подсветка остаётся только у карточек, которые всё ещё в ленте.
        retainMarketFeedNew(next.items.map((item) => item.id));
        // Индикатор отключён настройкой — показать его негде: бейдж гасим при
        // открытии ленты (пользователь список уже видит).
        if (!liveSettingsRef.current.inApp) acknowledgeMarketFeedNew();
      } catch (e) {
        if (cancelled) return;
        logErrorToJournal(e, pageRef.current === null
          ? 'Лента мастера: загрузка заявок'
          : 'Лента мастера: фоновая сверка заявок');
        const nextFailure = toMarketFailure(e);
        if (pageRef.current === null) setFailure(nextFailure);
        else setSyncError(nextFailure.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadedFilters, filtersReady, scope, userId, setFeedPage, tick]);

  const loadBalance = useCallback(async () => {
    try {
      setBalance(await loadMasterBalance());
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBalance();
  }, [balanceTick, loadBalance]);

  /** Перезагрузить ленту: вручную — с индикатором, фоном — тихо. */
  const requestReload = useCallback((silent: boolean) => {
    if (!silent) {
      setLoading(true);
      setFailure(null);
      setSyncError('');
    }
    setTick((n) => n + 1);
  }, []);

  /** Повторное чтение баланса с индикатором (кнопки «Повторить»). */
  const reloadBalance = useCallback(() => {
    setBalanceLoading(true);
    setBalanceTick((n) => n + 1);
  }, []);

  // Reconnect: пока связи не было, события терялись — после подтверждённого
  // online делаем ТИХУЮ сверку с сервером (лента не размонтируется).
  // Подписка на внешний источник состояния — обновление приходит из callback.
  useEffect(() => {
    return useConnectionStore.subscribe((state, prev) => {
      if (state.status !== 'online' || prev.status === 'online') return;
      if (pageRef.current === null) return;
      setTick((n) => n + 1);
    });
  }, []);

  // Живое обновление: одно событие = одна карточка (без полной перезагрузки).
  // Очередь гарантирует порядок (события одной заявки применяются по порядку).
  const applyLiveEvent = useCallback(async (event: MarketFeedLiveEvent) => {
    const current = pageRef.current;
    if (!current) return; // первая загрузка ещё идёт — её снапшот уже свежий
    const ctx = ctxRef.current;
    let aux = auxRef.current;
    if (!aux) {
      aux = await loadMarketFeedAuxiliary();
      auxRef.current = aux;
    }
    const item = buildMarketFeedLiveItem(event, ctx, aux);
    const result = mergeMarketFeedLiveEvent(current, event, ctx, item, MARKET_FEED_PAGE_SIZE);
    if (result.requiresResync) {
      setSyncError(t('market.sync_error'));
      requestReload(true);
      return;
    }
    if (result.page !== current) setFeedPage(result.page);
    if (result.removed) dropMarketFeedNew([event.id]);
    if (result.added && event.action === 'created') {
      noteNewMarketRequests([event.id], { actorId: event.actorId, userId: ctx.userId });
    }
  }, [requestReload, setFeedPage, t]);

  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      const parsed = parseMarketFeedLiveEvent(ev);
      if (!parsed) return;
      liveQueueRef.current = liveQueueRef.current
        .then(() => applyLiveEvent(parsed))
        .catch((e) => logErrorToJournal(e, 'Лента мастера: живое событие'));
    });
  }, [applyLiveEvent]);

  /** Изменить часть отборов (значение сразу в форме, сохранение — эффектом). */
  const applyFilters = useCallback((patch: Partial<MarketFeedFilterValues>) => {
    acknowledgeMarketFeedNew();
    setFilters((prev) => normalizeMarketFeedFilters({ ...prev, ...patch }));
    // Ввод в поиск не размонтирует список: результат заменится после паузы,
    // иначе форма мигала бы на каждой букве.
    const queryOnly = Object.keys(patch).length === 1 && 'query' in patch;
    setLoading(true);
    setFailure(null);
    setSyncError('');
    if (!queryOnly) setFeedPage(null);
  }, [setFeedPage]);

  /** Сбросить ВСЕ отборы (включая сохранённые): пустой набор тоже сохраняется. */
  const resetAllFilters = useCallback(() => {
    acknowledgeMarketFeedNew();
    geoPrefilledRef.current = true;
    savedFiltersRef.current = true;
    setFilters({ ...EMPTY_MARKET_FEED_FILTERS, geo: { ...EMPTY_MARKET_GEO } });
    setGeoLabels({});
    setFeedPage(null);
    setLoading(true);
    setFailure(null);
    setSyncError('');
  }, [setFeedPage]);

  // Центр для подписи расстояния в карточках (координаты отбора).
  const geoOrigin = useMemo(() => {
    const filter = marketGeoFilter(feedFilters.geo);
    return filter.lat !== null && filter.lon !== null
      ? { lat: filter.lat, lon: filter.lon }
      : null;
  }, [feedFilters.geo]);

  const switchScope = (next: string) => {
    const id = next === 'mine' ? 'mine' : 'feed';
    if (id === scope) return;
    acknowledgeMarketFeedNew();
    setScope(id);
    setFeedPage(null);
    setLoading(true);
    setFailure(null);
    setSyncError('');
    setTakeFailures({});
  };

  /** Прочитать новые заявки: индикатор/бейдж обнуляются, лента — к первой карточке. */
  const showNewRequests = () => {
    acknowledgeMarketFeedNew();
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const take = async (item: MarketFeedItem) => {
    setTakingId(item.id);
    setTakeFailures((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    const result = await takeMarketRequest(item.id, userId);
    const takeFailure = isTakeFailure(result) ? result : null;
    if (!takeFailure) {
      showSuccess(t('market.request_taken'));
      dropMarketFeedNew([item.id]);
      // В ленте «только новые» взятая заявка отбору больше не соответствует.
      const hideFromNewFeed = scope === 'feed' && feedFilters.onlyNew;
      const current = pageRef.current;
      if (current) {
        if (hideFromNewFeed) {
          setFeedPage({
            items: current.items.filter((x) => x.id !== item.id),
            total: Math.max(current.items.length - 1, current.total - 1),
          });
        } else {
          // Перечитываем ОДНУ заявку: сервер уже отдаёт раскрытые контакты
          // взявшему мастеру. Если чтение не удалось (связь), карточка останется
          // в прежнем состоянии — живое событие/reconnect доведут её.
          try {
            const fresh = await loadMarketRequest(item.id);
            if (fresh.contactRevealed) {
              const latest = pageRef.current;
              if (latest) {
                setFeedPage({
                  total: latest.total,
                  items: latest.items.map((x) => (x.id === item.id
                    ? {
                        ...x,
                        ...fresh,
                        takePriceValue: x.takePriceValue,
                        isMine: !!userId && fresh.masterId === userId,
                        isTakenByOther: false,
                      }
                    : x)),
                });
              }
            }
          } catch (e) {
            logErrorToJournal(e, 'Лента мастера: чтение контактов после взятия');
          }
        }
      }
      setBalanceTick((n) => n + 1);
    } else {
      setTakeFailures((prev) => ({ ...prev, [item.id]: takeFailure }));
      if (takeFailure.kind === 'already-taken') requestReload(true);
      // Телефон не подтверждён: карточка остаётся с одним понятным блоком
      // (без тоста и без второго сообщения), фоново сверяем серверные метки.
      if (takeFailure.kind === 'phone-unverified') requestReload(true);
      if (takeFailure.kind === 'no-money' || takeFailure.kind === 'billing-disabled') {
        setBalanceTick((n) => n + 1);
      }
      logErrorToJournal(
        new Error(takeFailure.message),
        `Лента мастера: взятие заявки (${takeFailure.kind})`,
      );
    }
    setTakingId('');
  };

  const offline = connection === 'offline';
  const takeBlockedReason = !userId
    ? t('market.master_login_needed')
    : offline
      ? t('market.master_offline_take')
      : balance && isBalanceFailure(balance) && balance.kind === 'disabled'
        ? t('market.master_billing_off')
        : undefined;

  const items = page?.items || [];
  const total = page?.total || 0;
  const sourceKey = marketFeedSourceKey(scope, userId, loadedFilters || feedFilters);
  const showInitialLoading = loading && page === null;
  const highlighted = liveSettings.inApp ? newIds : [];

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.feed')}
        subtitle={scope === 'feed' ? t('market.feed_subtitle_free') : t('market.feed_subtitle_mine')}
        onBack={onBack}
        action={(
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg px-2 text-xs"
              onClick={() => {
                acknowledgeMarketFeedNew();
                requestReload(false);
                setBalanceTick((n) => n + 1);
              }}
              disabled={loading}
              aria-label={t('market.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 rounded-lg p-0"
              onClick={onOpenArea}
              aria-label={t('market.service_area')}
              title={t('market.service_area')}
            >
              <MapPin className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 rounded-lg p-0"
              onClick={onOpenProfile}
              aria-label={t('market.profile')}
              title={t('market.profile')}
            >
              <UserRound className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-3 p-4 pb-24 sm:p-6">
          <OfflineDataBanner sourceKey={sourceKey} />

          <MarketFeedLiveBanner
            connection={connection}
            unseen={newIds.length}
            showUnseen={liveSettings.inApp}
            syncError={syncError || undefined}
            onShowNew={showNewRequests}
            onRetry={() => requestReload(false)}
          />

          <MarketBalanceCard
            state={balance}
            loading={balanceLoading}
            onRetry={reloadBalance}
            onOpenWallet={onOpenWallet}
          />

          <TabBar
            group="market-master-scope"
            tabs={[
              { id: 'feed', label: t('market.free_requests') },
              { id: 'mine', label: t('market.taken_requests') },
            ]}
            active={scope}
            onChange={switchScope}
          />

          <MarketFeedFilters
            categories={categories}
            services={services}
            filters={feedFilters}
            geoLabels={geoLabels}
            showOnlyNew={scope === 'feed'}
            showDataLimitations={scope === 'feed'}
            total={total}
            loading={loading}
            onChange={applyFilters}
            onResetAll={resetAllFilters}
            onRefresh={() => {
              acknowledgeMarketFeedNew();
              requestReload(false);
            }}
          />

          <div ref={listTopRef} className="scroll-mt-2" aria-hidden="true" />

          {failure ? (
            <MarketError failure={failure} onRetry={() => requestReload(false)} />
          ) : showInitialLoading ? (
            <MarketLoading text={scope === 'feed' ? t('market.feed_loading_free') : t('market.feed_loading_mine')} />
          ) : items.length === 0 ? (
            scope === 'feed' ? (
              <MarketEmpty
                icon={<Inbox className="h-5 w-5" />}
                title={t('market.no_free')}
                text="Измените отборы или нажмите «Сбросить всё» — новые заявки появятся здесь автоматически."
                action={(
                  <Button
                    variant="outline"
                    onClick={resetAllFilters}
                  >
                    Показать все заявки
                  </Button>
                )}
              />
            ) : (
              <MarketEmpty
                icon={<ClipboardList className="h-5 w-5" />}
                title={t('market.not_taken')}
                text="Возьмите заявку из ленты — она появится здесь вместе с контактами и чатом."
                action={(
                  <Button variant="outline" onClick={() => switchScope('feed')}>
                    Перейти в ленту
                  </Button>
                )}
              />
            )
          ) : (
            <>
              <div className="space-y-2.5">
                {items.map((item) => (
                  <MarketFeedCard
                    key={item.id}
                    item={item}
                    busy={takingId === item.id}
                    failure={takeFailures[item.id] || null}
                    currency={balance && !isBalanceFailure(balance) ? balance.balance.currency : undefined}
                    origin={geoOrigin}
                    takeBlockedReason={takeBlockedReason}
                    highlight={highlighted.includes(item.id)}
                    onTake={() => void take(item)}
                    onOpenChat={() => onOpenChat(item)}
                  />
                ))}
              </div>
              {total > items.length && (
                <div className="rounded-xl border border-dashed px-3 py-2 text-center text-[11px] leading-5 text-muted-foreground">
                  Показаны первые {items.length} из {total}. Уточните отборы, чтобы увидеть
                  остальные заявки.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
